import type { NativeCrop } from "./native-crop";
import { createFormatPicker } from "./native-format-picker";
import { documentPoint, hitNode, type Camera } from "../native/geometry";
import type { NativeSession } from "../native/session";
import { applyPage, displayMeasurement, pixelsPerUnit } from "../native/page";

/** Uncommitted page fields and workspace preview never enter the document/history. */
export class NativePageInspector {
  onWorkspaceApply?: () => void;
  private panel = document.getElementById("native-page-properties")!;
  private warning = document.getElementById(
    "native-page-warning",
  ) as HTMLDialogElement;
  private formats: ReturnType<typeof createFormatPicker>;
  private opened = false;
  private revision = -1;
  private generation = -1;
  private unit = "px";
  private size = { width: 960, height: 640 };
  private pending: (() => void) | null = null;
  dirty = false;
  constructor(
    private session: NativeSession,
    activate: () => void,
    private changed: () => void,
    private message: (text: string, error?: boolean) => void,
    private blocked: () => boolean,
    private camera: Camera,
    private crop: NativeCrop,
  ) {
    crop.bindBase(
      (box) => {
        this.size = { width: box.width, height: box.height };
        this.measurements();
        this.refresh();
      },
      () => {
        this.opened = false;
        this.reset();
      },
    );
    document.getElementById("native-page-open")!.onclick = () => {
      if (blocked() || this.opened) return;
      activate();
      if (session.mode === "graphics") crop.activate("base");
      this.opened = true;
      this.reset();
      changed();
    };
    for (const key of ["width", "height"] as const)
      this.field(key).oninput = () => {
        this.size[key] =
          Number(this.field(key).value) * pixelsPerUnit(this.unit);
        this.refresh();
      };
    this.field("unit").onchange = () => {
      this.unit = this.field("unit").value;
      this.measurements();
      this.refresh();
    };
    this.formats = createFormatPicker({
      read: () => ({ ...this.size, generation: session.generation }),
      choose: (format) => {
        this.size = { width: format.width, height: format.height };
        this.unit = format.unit;
        this.field("unit").value = this.unit;
        this.measurements();
        if (format.widthMM && format.heightMM) {
          this.field("width").value = String(format.widthMM);
          this.field("height").value = String(format.heightMM);
        }
        this.refresh();
      },
    });
    document.getElementById("native-base-visible")!.onclick = () => {
      if (blocked()) return;
      activate();
      this.opened = true;
      session.document = replaceActivePage(session.document, {
        ...session.page,
        backgroundVisible: !session.page.backgroundVisible,
      });
      session.finish();
      crop.activate("base");
      this.opened = true;
      this.reset();
      changed();
    };
    this.field("color").oninput = () => this.refresh();
    for (const button of this.panel.querySelectorAll<HTMLButtonElement>(
      "[data-page-color]",
    ))
      button.onclick = () => {
        this.field("color").value = button.dataset.pageColor!;
        this.refresh();
      };
    document.getElementById("native-page-apply")!.onclick = () =>
      void this.apply();
    document.getElementById("native-page-confirm")!.onclick = async () => {
      if (await this.apply()) this.resolve();
    };
    document.getElementById("native-page-discard")!.onclick = () => {
      this.reset();
      this.resolve();
    };
    document.getElementById("native-page-stay")!.onclick = () => {
      this.pending = null;
      this.warning.close();
    };
    this.warning.addEventListener("cancel", () => {
      this.pending = null;
    });
    document.addEventListener(
      "pointerdown",
      (event) => {
        if (
          !this.dirty ||
          this.warning.open ||
          this.formats?.open ||
          (event.target === document.getElementById("native-overlay") &&
            crop.handle(event)) ||
          (event.target as HTMLElement).closest(
            "#native-page-properties,#native-page-open",
          )
        )
          return;
        const target = event.target as HTMLElement,
          x = event.clientX,
          y = event.clientY;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.ask(() => {
          if (target.id === "native-overlay") {
            const node = hitNode(
              this.session.page.nodes,
              documentPoint({ x, y }, this.camera),
            );
            crop.close();
            this.session.select(node ? [node.id] : []);
            this.opened = false;
          } else
            (
              target.closest<HTMLElement>("button,a,input,select") || target
            ).click();
        });
      },
      true,
    );
    document.addEventListener(
      "click",
      (event) => {
        if (
          this.warning.open &&
          !(event.target as HTMLElement).closest("#native-page-warning")
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      },
      true,
    );
    window.addEventListener(
      "keydown",
      (event) => {
        if (!this.dirty || this.warning.open || this.formats?.open) return;
        const inField = (event.target as HTMLElement).closest(
          "input,textarea,select,[contenteditable=true]",
        );
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.reset();
          this.changed();
          return;
        }
        if (inField) return;
        if (
          event.ctrlKey ||
          event.metaKey ||
          ["Delete", "Backspace"].includes(event.key)
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();
          const init = {
            key: event.key,
            code: event.code,
            ctrlKey: event.ctrlKey,
            metaKey: event.metaKey,
            shiftKey: event.shiftKey,
            bubbles: true,
            cancelable: true,
          };
          this.ask(() => {
            document.getElementById("native-overlay")!.focus();
            window.dispatchEvent(new KeyboardEvent("keydown", init));
          });
        }
      },
      true,
    );
  }
  get previewColor() {
    return this.opened && this.dirty
      ? this.field("color").value
      : this.session.page.workspaceColor;
  }
  private field(name: string) {
    return document.getElementById("native-page-" + name) as HTMLInputElement;
  }
  private measurements() {
    for (const key of ["width", "height"] as const) {
      const field = this.field(key);
      const physical = this.formats?.physical(
        this.size.width,
        this.size.height,
        this.unit,
      );
      field.value = physical
        ? String(physical[key])
        : displayMeasurement(this.size[key], this.unit);
      field.step = this.unit === "px" ? "1" : "0.1";
      field.parentElement!.querySelector("span")!.textContent =
        `${key === "width" ? "Ширина" : "Высота"}, ${{ px: "px", mm: "мм", cm: "см" }[this.unit]}`;
    }
  }
  private reset() {
    const page = this.session.page;
    this.size = { ...page.size };
    this.unit = page.unit;
    this.field("unit").value = this.unit;
    this.field("color").value = page.workspaceColor;
    this.measurements();
    this.revision = this.session.revision;
    this.generation = this.session.generation;
    if (this.crop.active && this.crop.source === "base") this.crop.reset();
    this.refresh();
  }
  private refresh() {
    const page = this.session.page;
    this.crop.setSize(this.size);
    this.dirty =
      (this.crop.source === "base" && this.crop.dirty) ||
      this.size.width !== page.size.width ||
      this.size.height !== page.size.height ||
      this.unit !== page.unit ||
      this.field("color").value !== page.workspaceColor;
    document.getElementById("native-page-apply")!.hidden = !this.dirty;
    this.formats?.sync(this.size.width, this.size.height);
    for (const button of this.panel.querySelectorAll<HTMLButtonElement>(
      "[data-page-color]",
    ))
      button.setAttribute(
        "aria-pressed",
        String(button.dataset.pageColor === this.field("color").value),
      );
    this.changed();
  }
  private async apply() {
    if (this.blocked() && !this.warning.open) return false;
    if (
      this.revision !== this.session.revision ||
      this.generation !== this.session.generation
    ) {
      this.reset();
      return false;
    }
    try {
      const colorChanged =
        this.field("color").value !== this.session.page.workspaceColor;
      const settings = applyPage(
        this.session.document,
        this.size,
        this.unit,
        this.field("color").value,
      );
      if (this.crop.active && this.crop.source === "base") {
        const result = await this.crop.prepare(settings);
        this.session.document = result.document;
        this.session.finish();
        this.crop.complete(result);
      } else {
        this.session.document = settings;
        this.session.finish();
      }
      if (colorChanged) this.onWorkspaceApply?.();
      this.reset();
      this.message("Параметры холста применены.");
      return true;
    } catch (error) {
      this.message((error as Error).message, true);
      return false;
    }
  }
  requestLeave(action: () => void) {
    if (this.dirty) this.ask(action);
    else action();
  }
  private ask(action: () => void) {
    this.pending = action;
    this.warning.showModal();
  }
  private resolve() {
    const action = this.pending;
    this.pending = null;
    this.warning.close();
    action?.();
    this.changed();
  }
  render() {
    if (
      this.revision !== this.session.revision ||
      this.generation !== this.session.generation
    ) {
      if (this.opened) this.reset();
    }
    if (this.session.selectedIds.length) this.opened = false;
    this.panel.hidden = !this.opened;
    const base = document.getElementById("native-base-layer")!;
    base.dataset.selected = String(this.opened);
    base.classList.toggle("is-hidden", !this.session.page.backgroundVisible);
    const eye = document.getElementById("native-base-visible")!;
    const visible = this.session.page.backgroundVisible;
    eye.setAttribute("aria-pressed", String(visible));
    eye.setAttribute(
      "aria-label",
      visible ? "Скрыть базовый слой" : "Показать базовый слой",
    );
    eye.title = eye.getAttribute("aria-label")!;
    const path = visible
      ? "M12 4c7 0 10.5 8 10.5 8S19 20 12 20 1.5 12 1.5 12 5 4 12 4zm0 2c-4.5 0-7.4 4.4-8.2 6 .8 1.6 3.7 6 8.2 6s7.4-4.4 8.2-6C19.4 10.4 16.5 6 12 6zm0 2a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"
      : "M3.3 2 22 20.7 20.7 22l-3.1-3.1A11.4 11.4 0 0 1 12 20C5 20 1.5 12 1.5 12a20 20 0 0 1 4.2-5.4L2 3.3 3.3 2zm4 6.3A13.6 13.6 0 0 0 3.8 12c.9 1.6 3.8 6 8.2 6 1.4 0 2.7-.4 3.8-1l-2-2a4 4 0 0 1-5-5l-1.5-1.7zM12 4c7 0 10.5 8 10.5 8a19 19 0 0 1-2.7 4l-1.5-1.5a15.4 15.4 0 0 0 2-2.5C19.4 10.4 16.5 6 12 6c-.8 0-1.6.1-2.3.4L8.1 4.8C9.3 4.3 10.6 4 12 4zm0 4a4 4 0 0 1 4 4c0 .5-.1 1-.3 1.5l-5.2-5.2C11 8.1 11.5 8 12 8z";
    if (eye.dataset.visible !== String(visible)) {
      eye.dataset.visible = String(visible);
      eye.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${path}"/></svg>`;
    }

    document
      .getElementById("native-page-open")!
      .setAttribute("aria-pressed", String(this.opened));
    if (this.opened) {
      document.getElementById("native-selection-title")!.textContent =
        this.session.mode === "interactive" ? "Экран" : "Размер холста";
      document.getElementById("native-selection-count")!.textContent =
        this.session.mode === "interactive"
          ? this.session.page.name
          : "Базовый слой";
    }
  }
}
import { replaceActivePage } from "../native/pages";
